<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
} from "reka-ui";
import { computed, onBeforeUnmount, useId, watch } from "vue";

import { useWindow } from "@/hooks/use-window";

import { useCaseSelectionMenuStore } from "./case-selection-menu-store";

import type { SelectionMenuAction, SelectionMenuContribution } from "./case-selection-menu-store";

const store = useCaseSelectionMenuStore();
const window = useWindow();
const id = useId();

const sections = computed(() => {
  const snapshot = store.snapshot;
  if (snapshot == null) return [];

  const result: Array<{ contribution: SelectionMenuContribution; actions: readonly SelectionMenuAction[] }> = [];
  for (const contribution of store.contributions.values()) {
    const actions = contribution.getActions(snapshot);
    if (actions.length > 0) result.push({ contribution, actions });
  }

  return result.sort(
    ({ contribution: a }, { contribution: b }) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id),
  );
});

const reference = computed(() => store.snapshot?.anchor);

// Reka only restores focus to a trigger, and the menu has none; remember what had focus before it opened.
let previousFocus = null as HTMLElement | null;
watch(reference, (anchor) => {
  if (anchor != null) previousFocus = window.value.document.activeElement as HTMLElement | null;
});

function dismiss() {
  const snapshot = store.snapshot;
  if (snapshot != null) store.dismiss(snapshot.id);
}

async function runAction(contribution: SelectionMenuContribution, action: SelectionMenuAction) {
  const snapshot = store.snapshot;
  if (snapshot == null) return;

  // Reka closes the menu in a microtask after select; run on the next frame, once its focus trap is gone.
  await new Promise((resolve) => window.value.requestAnimationFrame(resolve));

  try {
    await action.run({ snapshot });
  } catch (error) {
    console.error(`Selection menu action "${contribution.id}:${action.id}" failed`, error);
  }
}

onBeforeUnmount(store.reset);
</script>
<template>
  <DropdownMenuRoot v-if="reference != null && sections.length > 0" default-open @update:open="dismiss">
    <DropdownMenuPortal :to="window.document.body">
      <DropdownMenuContent
        data-selection-menu
        aria-label="Selection actions"
        side="bottom"
        align="start"
        avoid-collisions
        :reference="reference"
        :side-offset="6"
        :collision-padding="8"
        class="tw-z-[999999] tw-flex tw-min-w-48 tw-max-w-xs tw-flex-col tw-gap-1 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-p-1 tw-text-xs tw-text-black tw-shadow-lg"
        @close-auto-focus="previousFocus?.focus()"
      >
        <template v-for="(section, index) in sections" :key="section.contribution.id">
          <DropdownMenuSeparator v-if="index > 0" class="tw-h-px tw-bg-gray-200" />
          <DropdownMenuGroup :aria-labelledby="`${id}-${section.contribution.id}`" class="tw-flex tw-flex-col">
            <DropdownMenuLabel
              :id="`${id}-${section.contribution.id}`"
              class="tw-px-2 tw-pb-0.5 tw-pt-1 tw-font-semibold tw-text-gray-500"
            >
              {{ section.contribution.label }}
            </DropdownMenuLabel>
            <DropdownMenuItem
              v-for="action in section.actions"
              :key="action.id"
              :text-value="action.label"
              :disabled="action.disabledReason != null"
              class="tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-rounded tw-px-2 tw-py-1.5 tw-outline-none data-[disabled]:tw-cursor-not-allowed data-[highlighted]:tw-bg-gray-100 data-[disabled]:tw-opacity-60"
              @select="runAction(section.contribution, action)"
            >
              <i v-if="action.icon" class="fa tw-mt-0.5 tw-w-3" :class="action.icon" aria-hidden="true" />
              <span class="tw-flex tw-min-w-0 tw-flex-col">
                <span class="tw-font-medium">{{ action.label }}</span>
                <span v-if="action.disabledReason" class="tw-text-gray-600">{{ action.disabledReason }}</span>
              </span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </template>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
